"use client";

import type React from "react";

import { ArrowRight, CheckCircle, ChevronRight, CreditCard, type LucideIcon, MessageSquare, Phone, ShieldCheck, ShoppingBag, Smartphone, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

export default function Home() {
  const t = useTranslations("home");
  const tGeneral = useTranslations("general");
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // doğrudan login sayfasına yönlendir
  useEffect(() => {
    window.location.href = "/Auth/Login";
  }, []);

  const carouselItems = [
    {
      title: t("carousel.0.title"),
      description: t("carousel.0.description"),
      image: "/placeholder.svg?height=500&width=500",
      color: "from-black to-red-600 dark:from-gray-900 dark:to-red-700",
    },
    {
      title: t("carousel.1.title"),
      description: t("carousel.1.description"),
      image: "/placeholder.svg?height=500&width=500",
      color: "from-red-600 to-orange-400 dark:from-red-700 dark:to-orange-500",
    },
    {
      title: t("carousel.2.title"),
      description: t("carousel.2.description"),
      image: "/placeholder.svg?height=500&width=500",
      color: "from-gray-800 to-gray-500 dark:from-gray-900 dark:to-gray-600",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;

      setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  useEffect(() => {
    if (!isDragging) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
      }, 5000);

      return () => clearInterval(interval);
    }
    return () => { };
  }, [carouselItems.length, isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const endX = e.clientX;
    const diffX = endX - startX;

    if (diffX > 100) {
      // Swiped right
      setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
    } else if (diffX < -100) {
      // Swiped left
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }

    setIsDragging(false);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const endX = e.changedTouches[0].clientX;
    const diffX = endX - startX;

    if (diffX > 100) {
      // Swiped right
      setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
    } else if (diffX < -100) {
      // Swiped left
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }

    setIsDragging(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (consentChecked) {
      // In a real app, you would send this to your backend
      // Reset form after 3 seconds
      setTimeout(() => {
        setConsentChecked(false);
        setContactOpen(false);
      }, 3000);
    }
  };

  return (
    <div className="flex min-h-screen flex-col relative">
      {/* Fixed Contact Form at Bottom */}
      <div className={cn("fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 transform", contactOpen ? "translate-y-0" : "translate-y-[calc(100%-60px)]")}>
        <div className="mx-auto max-w-lg shadow-lg rounded-t-lg overflow-hidden bg-red-600 dark:bg-red-700">
          <button onClick={() => setContactOpen(!contactOpen)} className="flex items-center justify-center p-4 text-white font-medium cursor-pointer w-full h-[60px]">
            {contactOpen ? (
              <>
                <X className="h-6 w-6 mr-3" />
                <span className="text-lg">{t("callback.close")}</span>
              </>
            ) : (
              <>
                <Phone className="h-6 w-6 mr-3" />
                <span className="text-lg font-bold">{t("callback.title")}</span>
              </>
            )}
          </button>

          <div className={cn("bg-card text-card-foreground transition-all duration-300 overflow-hidden p-6", contactOpen ? "max-h-[400px]" : "max-h-0")}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-lg font-bold">{t("callback.we_call_back")}</div>
              <p className="text-muted-foreground">{t("callback.leave_phone")}</p>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base">
                  {tGeneral("phone")}
                </Label>
                <Input id="phone" placeholder={t("callback.phone_placeholder")} className="w-full text-base p-3 h-12 ring-red-600 dark:ring-red-500" />
              </div>

              <div className="flex items-start space-x-2 pt-2">
                <Checkbox id="consent" checked={consentChecked} onCheckedChange={(checked) => setConsentChecked(checked === true)} className="mt-1" />
                <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
                  {t("callback.consent")}
                </Label>
              </div>

              <Button type="submit" className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 w-full py-6 text-base font-medium text-white" disabled={!consentChecked}>
                <Phone className="h-5 w-5 mr-2" />
                {t("callback.request_button")}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <main className="flex-1">
        {/* Carousel Hero Section with Scale Transitions and Mouse Drag */}
        <section className="w-full py-20 md:py-32 bg-gradient-to-b from-background to-muted/50 relative overflow-hidden">
          <div className="container mx-auto px-4 md:px-6 relative">
            <div
              ref={carouselRef}
              className="relative min-h-[500px] md:min-h-[600px] cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {carouselItems.map((item, index) => (
                <div key={index} className={cn("absolute inset-0 transition-all duration-1000 ease-in-out", currentSlide === index ? "opacity-100 scale-100 z-10" : "opacity-0 scale-95 z-0")}>
                  <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center h-full">
                    <div className="space-y-6">
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">{item.title}</h1>
                      <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70 leading-relaxed">{item.description}</p>
                      <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <Button className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-lg py-6 px-8 text-white">
                          {t("discover_services")}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button variant="outline" className="text-lg py-6 px-8" onClick={() => setContactOpen(true)}>
                          {t("get_in_touch")}
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="relative w-full max-w-[500px] aspect-square">
                        <div className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-lg opacity-10 blur-3xl`}></div>
                        <Image src={item.image || "/placeholder.svg"} alt="Digital Business Solutions" className="relative z-10 mx-auto rounded-lg object-cover shadow-lg" width={500} height={500} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Parallax Service Sections */}
        <ParallaxSection
          id="mobile"
          icon={Smartphone}
          title={t("services.mobile.title")}
          description={t("services.mobile.description")}
          features={[
            t("services.mobile.features.0"),
            t("services.mobile.features.1"),
            t("services.mobile.features.2"),
            t("services.mobile.features.3")
          ]}
          imageSrc="/placeholder.svg?height=400&width=600"
          imageAlt="Mobile phone credit and game cards"
          bgColor="bg-background"
          serviceLink="/Services/mobile"
        />

        <ParallaxSection
          id="ecommerce"
          icon={ShoppingBag}
          title={t("services.ecommerce.title")}
          description={t("services.ecommerce.description")}
          features={[
            t("services.ecommerce.features.0"),
            t("services.ecommerce.features.1"),
            t("services.ecommerce.features.2"),
            t("services.ecommerce.features.3")
          ]}
          imageSrc="/placeholder.svg?height=400&width=600"
          imageAlt="E-commerce mobile phone products"
          bgColor="bg-muted/50"
          reverse={true}
          serviceLink="/Services/ecommerce"
        />

        <ParallaxSection
          id="b2b"
          icon={ShieldCheck}
          title={t("services.b2b.title")}
          description={t("services.b2b.description")}
          features={[
            t("services.b2b.features.0"),
            t("services.b2b.features.1"),
            t("services.b2b.features.2"),
            t("services.b2b.features.3")
          ]}
          imageSrc="/placeholder.svg?height=400&width=600"
          imageAlt="B2B software solutions"
          bgColor="bg-background"
          serviceLink="/Services/b2b"
        />

        <ParallaxSection
          id="invoice"
          icon={MessageSquare}
          title={t("services.invoice.title")}
          description={t("services.invoice.description")}
          features={[
            t("services.invoice.features.0"),
            t("services.invoice.features.1"),
            t("services.invoice.features.2"),
            t("services.invoice.features.3")
          ]}
          imageSrc="/placeholder.svg?height=400&width=600"
          imageAlt="E-invoice services"
          bgColor="bg-muted/50"
          reverse={true}
          serviceLink="/Services/invoice"
        />

        <ParallaxSection
          id="payment"
          icon={CreditCard}
          title={t("services.payment.title")}
          description={t("services.payment.description")}
          features={[
            t("services.payment.features.0"),
            t("services.payment.features.1"),
            t("services.payment.features.2"),
            t("services.payment.features.3")
          ]}
          imageSrc="/placeholder.svg?height=400&width=600"
          imageAlt="Online payment solutions"
          bgColor="bg-background"
          serviceLink="/Services/payment"
        />

        <section id="contact" className="w-full py-20 md:py-32 bg-black text-white dark:bg-gray-950">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-8 text-center">
              <div className="space-y-4">
                <div className="inline-block rounded-lg bg-red-600 dark:bg-red-700 px-4 py-2 text-base">{t("ready_to_work.badge")}</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">{t("ready_to_work.title")}</h2>
                <p className="max-w-[900px] text-xl md:text-2xl text-gray-300 leading-relaxed">{t("ready_to_work.description")}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-lg py-6 px-8 text-white" onClick={() => setContactOpen(true)}>
                  {t("ready_to_work.contact_now")}
                </Button>
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-black dark:border-gray-300 dark:text-gray-300 dark:hover:bg-gray-300 dark:hover:text-gray-950 text-lg py-6 px-8"
                >
                  {t("ready_to_work.explore_services")}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

interface ParallaxSectionProps {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
  imageSrc: string;
  imageAlt: string;
  bgColor: string;
  serviceLink: string;
  reverse?: boolean;
}

function ParallaxSection({ id, icon: Icon, title, description, features, imageSrc, imageAlt, bgColor, serviceLink, reverse = false }: ParallaxSectionProps) {
  const t = useTranslations("home.services");
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !contentRef.current || !imageRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const scrollPosition = window.innerHeight - rect.top;

      if (scrollPosition > 0 && scrollPosition < window.innerHeight + rect.height) {
        // Calculate parallax effect
        const parallaxValue = (scrollPosition / (window.innerHeight + rect.height)) * 100;

        // Apply different speeds to content and image
        contentRef.current.style.transform = `translateY(${parallaxValue * 0.15}px)`;
        imageRef.current.style.transform = `translateY(${parallaxValue * -0.2}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section id={id} ref={sectionRef} className={`w-full py-20 md:py-32 ${bgColor} overflow-hidden relative`}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div
          className={cn(
            "grid gap-12 lg:grid-cols-2 lg:gap-16 items-center transition-all duration-700",
            reverse ? "lg:grid-flow-col-dense" : "",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          )}
        >
          <div ref={contentRef} className={cn("space-y-6 transition-all duration-500 will-change-transform", reverse ? "lg:order-last" : "")}>
            <div className="inline-flex items-center gap-2 rounded-lg bg-card px-4 py-2 text-base">
              <Icon className="h-5 w-5 text-red-600 dark:text-red-500" />
              <span className="font-medium">{title}</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter">{title}</h2>
            <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70 leading-relaxed">{description}</p>
            <ul className="grid gap-4">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 transition-all duration-300 hover:translate-x-1">
                  <CheckCircle className="h-6 w-6 text-red-600 dark:text-red-500 flex-shrink-0" />
                  <span className="text-lg">{feature}</span>
                </li>
              ))}
            </ul>
            <div className="pt-4">
              <Link href={serviceLink}>
                <Button className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 transition-all duration-300 hover:shadow-lg group text-lg py-6 px-8 text-white">
                  {t("more_info")}
                  <ChevronRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
          <div ref={imageRef} className="flex justify-center overflow-hidden rounded-xl will-change-transform">
            <Link href={serviceLink} className="relative w-full overflow-hidden rounded-xl group">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-black/20 dark:from-red-700/20 dark:to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
              <Image
                src={imageSrc || "/placeholder.svg"}
                alt={imageAlt}
                className="mx-auto aspect-video overflow-hidden rounded-xl object-cover object-center sm:w-full transition-transform duration-700 group-hover:scale-105 shadow-lg"
                width={600}
                height={400}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Parallax Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute -top-20 -left-20 w-40 h-40 rounded-full bg-red-600/5 dark:bg-red-700/5 blur-3xl transform ${reverse ? "translate-y-[30%]" : "translate-y-[10%]"}`}
          style={{
            transform: `translateY(${reverse ? "30%" : "10%"}) scale(${reverse ? 1.5 : 1})`,
            opacity: 0.7,
          }}
        ></div>
        <div
          className={`absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-red-600/5 dark:bg-red-700/5 blur-3xl transform ${reverse ? "translate-y-[-20%]" : "translate-y-[-10%]"}`}
          style={{
            transform: `translateY(${reverse ? "-20%" : "-10%"}) scale(${reverse ? 1.2 : 1.5})`,
            opacity: 0.5,
          }}
        ></div>
      </div>
    </section>
  );
}
